import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../utils/cn';
import { Button } from './Button';
import { Select } from './Select';

export interface LocalPaginationProps {
  label: string;
  page: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

export function LocalPagination({
  label,
  page,
  pageSize,
  totalItems,
  pageSizeOptions = [5, 10, 15, 20, 50],
  onPageChange,
  onPageSizeChange,
  className,
}: LocalPaginationProps) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const visibleTotalPages = Math.max(1, totalPages);
  const firstItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem = Math.min(totalItems, page * pageSize);

  return (
    <div className={cn('flex flex-col gap-3 border-t border-ink-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      <div className="flex flex-wrap items-center gap-3 text-sm text-ink-600">
        <span>Hiển thị {firstItem}-{lastItem} / {totalItems}</span>
        <label className="flex items-center gap-2">
          <span className="text-ink-500">Số dòng</span>
          <Select
            value={String(pageSize)}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label={`Số dòng mỗi trang ${label}`}
            className="min-h-9 w-20 py-1 text-sm"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
        </label>
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <span className="text-sm font-medium text-ink-700">
          Trang {totalItems === 0 ? 0 : page}/{totalItems === 0 ? 0 : visibleTotalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1 || totalItems === 0}
            icon={<ChevronLeft className="h-4 w-4" />}
            aria-label={`Trang trước ${label}`}
            title="Trang trước"
          />
          <Button
            variant="secondary"
            size="icon"
            onClick={() => onPageChange(Math.min(visibleTotalPages, page + 1))}
            disabled={page >= visibleTotalPages || totalItems === 0}
            icon={<ChevronRight className="h-4 w-4" />}
            aria-label={`Trang sau ${label}`}
            title="Trang sau"
          />
        </div>
      </div>
    </div>
  );
}

export function useLocalPagination<T>(
  items: T[],
  defaultPageSize: number,
  resetKey = '',
) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSizeState] = React.useState(defaultPageSize);
  const totalPages = Math.ceil(items.length / pageSize);
  const safeTotalPages = Math.max(1, totalPages);

  React.useEffect(() => {
    setPage(1);
  }, [resetKey]);

  React.useEffect(() => {
    setPage((current) => Math.min(Math.max(1, current), safeTotalPages));
  }, [safeTotalPages]);

  const setPageSize = React.useCallback((nextPageSize: number) => {
    setPageSizeState(nextPageSize);
    setPage(1);
  }, []);

  const pagedItems = React.useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );

  return {
    page,
    pageSize,
    totalPages,
    pagedItems,
    setPage,
    setPageSize,
  };
}
